import { useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Mail, ArrowLeft, Sparkles, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { stripBidiControls } from "../lib/sanitize";

/**
 * /auth/forgot-password — sends a reset link via Email (Resend) OR SMS (Twilio).
 *
 * SMS path is the recommended one for FlowUp super-admins because the
 * Resend account is in test-mode and silently drops emails to non-owner
 * addresses. SMS uses Twilio and works for any phone on file.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState("sms");  // "sms" | "email"
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const cleanEmail = stripBidiControls(email).trim();
    if (!cleanEmail) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: cleanEmail, channel });
      setSent(true);
      toast.success(
        channel === "sms"
          ? "אם הטלפון רשום — שלחנו SMS עם קישור איפוס"
          : "אם האימייל קיים — שלחנו לינק איפוס"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "שגיאה");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-bl from-brand-50 via-white to-stone-50">
      <Toaster position="top-center" richColors dir="rtl" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mb-6" data-testid="forgot-back-login">
          <ArrowLeft className="h-3.5 w-3.5" />
          חזרה למסך התחברות
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-8 space-y-5" dir="rtl">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold">
              <Sparkles className="h-3 w-3" />
              FlowUp
            </div>
            <h1 className="font-heading text-3xl font-bold">שכחת סיסמה?</h1>
            <p className="text-sm text-stone-600">בחר/י איך לקבל קישור איפוס — SMS מומלץ</p>
          </div>

          {sent ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center space-y-2" data-testid="forgot-sent-block">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
              <p className="font-bold text-emerald-900">
                {channel === "sms" ? "בדקי את הסמסים שלך" : "בדקי את תיבת המייל שלך"}
              </p>
              <p className="text-xs text-emerald-700">
                אם יש חשבון רשום עם <strong dir="ltr">{email}</strong> —{" "}
                {channel === "sms"
                  ? "שלחנו SMS עם קישור איפוס לטלפון הרשום במערכת."
                  : "שלחנו אליו קישור לאיפוס סיסמה."}
                <br/>
                הקישור תקף לשעה אחת.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              {/* Channel toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-stone-100 rounded-xl" data-testid="forgot-channel-toggle">
                <button
                  type="button"
                  onClick={() => setChannel("sms")}
                  className={`px-3 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-1.5 ${
                    channel === "sms"
                      ? "bg-emerald-600 text-white shadow"
                      : "text-stone-600 hover:bg-white"
                  }`}
                  data-testid="forgot-channel-sms"
                >
                  <MessageSquare className="h-4 w-4" />
                  SMS לטלפון
                </button>
                <button
                  type="button"
                  onClick={() => setChannel("email")}
                  className={`px-3 py-2 rounded-lg text-sm font-bold transition flex items-center justify-center gap-1.5 ${
                    channel === "email"
                      ? "bg-brand text-white shadow"
                      : "text-stone-600 hover:bg-white"
                  }`}
                  data-testid="forgot-channel-email"
                >
                  <Mail className="h-4 w-4" />
                  מייל
                </button>
              </div>

              <div>
                <Label>אימייל החשבון</Label>
                <Input
                  type="text"
                  inputMode="email"
                  dir="ltr"
                  lang="en"
                  spellCheck={false}
                  autoComplete="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(stripBidiControls(e.target.value))}
                  placeholder="you@business.com"
                  className="mt-1 text-right"
                  required
                  autoFocus
                  data-testid="forgot-email-input"
                />
                <p className="text-[11px] text-stone-500 mt-1">
                  {channel === "sms"
                    ? "נשלח SMS למספר הטלפון השמור בחשבון הזה"
                    : "נשלח מייל לכתובת הזו"}
                </p>
              </div>

              <Button
                type="submit"
                disabled={busy}
                className={`w-full font-semibold ${channel === "sms" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-hover"} text-white`}
                data-testid="forgot-submit-btn"
              >
                {channel === "sms" ? <MessageSquare className="h-4 w-4 ml-2" /> : <Mail className="h-4 w-4 ml-2" />}
                {busy ? "שולח..." : (channel === "sms" ? "שלח SMS עם קישור איפוס" : "שלח קישור איפוס למייל")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
